/**
 * ComplexityController — the gameplay component that drives FlowBot.
 *
 * Composes four collaborators, each independently testable:
 *   - `complexityModel`   — the pure oracle recurrence (verdicts → band).
 *   - `AssessmentStream`  — the indexed assessment sequence; owns PAD threading.
 *   - `flowRegeneration`  — rest-phrase policy (stuck + accuracy → inject?).
 *   - `complexityMachine` — lifecycle (idle / active / restPending).
 *
 * The controller itself holds almost no state: it wires those four together and
 * exposes a small imperative surface for the scene. Everything numeric lives in
 * the stream or is recomputed from it, so there is no second source of truth to
 * drift.
 *
 * Instantiate behind `FeatureFlag.USE_COMPLEXITY_ADJUSTMENT` — when the flag is
 * off the controller simply is not constructed, and no complexity behaviour can
 * reach the scene.
 *
 * ## On PAD
 *
 * PAD is NOT applied here. The model already encodes it: the band for
 * assessment phrase `p` is driven by `verdict[p - pad]`. The render pipeline
 * supplies exactly that delay for free — `PhraseManager` requests the next
 * phrase at phrase end and consumes it at measure 2 of the following phrase, so
 * a phrase requested at the end of `p` first plays as `p + pad`. Deferring the
 * verdict again inside the controller would double-apply the delay.
 * {@link planNextPhrase} asserts the invariant.
 */

import { bandFor, classify, computeTrajectory } from './complexityModel';
import {
  AccuracySource,
  AssessmentBand,
  DEFAULT_TIER_CONFIG,
  TierConfig,
  Verdict,
} from './complexityModel.types';
import { AssessmentStream, PhraseKind } from './AssessmentStream';
import { GradedNote, tallyPhraseErrors } from './errorTally';
import { restBand, shouldInjectRest } from './flowRegeneration';
import { NwcsBand, SessionComplexityMap, TierComplexityScore } from './SessionComplexityMap';
import { complexityMachine } from './complexityMachine';
import type { ComplexityPhase } from './complexityMachine.types';
import { createActor, type ActorRefFrom } from 'xstate';

/** What the request path should fetch next. */
export interface PhrasePlan {
  /** Assessment or rest. Rest phrases consume no assessment index. */
  kind: PhraseKind;
  /** Complexity band to request, in normalized [0, 1]. */
  crMin: number;
  crMax: number;
  /** Assessment index this phrase will take, or `undefined` for a rest phrase. */
  idx?: number;
  /**
   * The same band denormalized to raw NWCS for the backend, or `undefined` when
   * the session map is not loaded — in which case the request carries no
   * complexity filter and the backend selects as it did pre-feature.
   */
  requestBand?: NwcsBand;
}

/** Emitted whenever a graded phrase moves (or holds) the band. */
export interface BandChange {
  /** Band now in effect for the next assessment phrase. */
  band: AssessmentBand;
  /** The verdict that caused this evaluation. */
  verdict: Verdict;
  /** Complexity error tally behind the verdict. */
  errorTotal: number;
  /** Assessment index that was just graded. */
  gradedIdx: number;
}

export interface ComplexityControllerConfig {
  /** Per-tier tunables. Defaults to the oracle's active preset. */
  tierConfig?: TierConfig;
  /** Live accuracy provider. See the WMA note on {@link AccuracySource}. */
  accuracySource: AccuracySource;
  /** Fired after each graded phrase, with the band now in effect. */
  onBandChanged?: (change: BandChange) => void;
  /** Fired when a rest phrase is planned, with the band it is drawn from. */
  onRestInjected?: (band: Readonly<{ crMin: number; crMax: number }>) => void;
  /** Diagnostics sink. Defaults to a no-op so tests stay quiet. */
  logger?: (message: string) => void;
}

export default class ComplexityController {
  private readonly config: TierConfig;
  private readonly accuracySource: AccuracySource;
  private readonly onBandChanged?: (change: BandChange) => void;
  private readonly onRestInjected?: (band: Readonly<{ crMin: number; crMax: number }>) => void;
  private readonly log: (message: string) => void;
  private readonly stream = new AssessmentStream();
  private readonly actor: ActorRefFrom<typeof complexityMachine>;
  private readonly sessionMap = new SessionComplexityMap();

  constructor({
    tierConfig = DEFAULT_TIER_CONFIG,
    accuracySource,
    onBandChanged,
    onRestInjected,
    logger,
  }: ComplexityControllerConfig) {
    this.config = tierConfig;
    this.accuracySource = accuracySource;
    this.onBandChanged = onBandChanged;
    this.onRestInjected = onRestInjected;
    this.log = logger ?? (() => undefined);
    this.actor = createActor(complexityMachine);
    this.actor.start();
  }

  /** Begin a session. Until this is called nothing may be planned or graded. */
  startSession(): void {
    this.actor.send({ type: 'SESSION_STARTED' });
    this.log(`[FlowBot] session started, seed floor=${this.config.complexityStart}`);
  }

  /**
   * Load the tier's raw NWCS scores so planned bands can be denormalized for the
   * backend. Until this is called, plans carry no `requestBand` and phrase
   * selection is unaffected — the controller still tracks the band internally.
   *
   * Safe to call more than once (e.g. a mid-session tier change); the newest
   * scores replace the old.
   */
  loadSessionMap(scores: readonly TierComplexityScore[]): void {
    this.sessionMap.load(scores);
    this.log(
      `[FlowBot] session map ${this.sessionMap.isLoaded() ? `loaded (${this.sessionMap.size()} scored phrases)` : 'not usable — complexity filtering disabled'}`,
    );
  }

  /** True once a usable session map has been loaded. */
  hasSessionMap(): boolean {
    return this.sessionMap.isLoaded();
  }

  /**
   * Decide what to fetch next, and register it in the assessment stream.
   *
   * Call this once per phrase request, at the point the scene asks for a new
   * phrase. The returned plan's `requestBand` is what the request path should
   * filter on (raw NWCS, or undefined when the session map is not loaded).
   *
   * @param phraseKey  Stable id for this request — the render pipeline's
   *                   `sequenceId`. The same key must be passed to
   *                   {@link ComplexityController.onPhraseEnd} when the phrase
   *                   finishes, so plan and grade line up regardless of any
   *                   phrase-uuid reuse from repeat-avoidance.
   * @throws if the session has not started.
   */
  planNextPhrase(phraseKey: string): PhrasePlan {
    if (this.getPhase() === 'idle') {
      throw new Error('ComplexityController: planNextPhrase before startSession');
    }

    const bands = this.getTrajectory();
    const wantsRest =
      this.getPhase() === 'active' &&
      shouldInjectRest(
        {
          bands,
          accuracy: this.accuracySource.currentAccuracy(),
          assessmentsSinceLastRest: this.stream.getAssessmentsSinceLastRest(),
        },
        this.config,
      );

    if (wantsRest) {
      const currentFloor =
        bands.length > 0 ? bands[bands.length - 1].crMin : this.config.complexityStart;
      const band = restBand(currentFloor, this.config);
      this.stream.register(phraseKey, PhraseKind.Rest);
      this.actor.send({ type: 'REST_INJECTED' });
      this.onRestInjected?.(band);
      this.log(
        `[FlowBot] rest phrase injected at ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)}`,
      );
      return {
        kind: PhraseKind.Rest,
        crMin: band.crMin,
        crMax: band.crMax,
        requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
      };
    }

    const idx = this.stream.getNextIdx();
    const band = bandFor(idx, this.stream.getVerdicts(), this.config);

    // PAD invariant: the band for phrase `idx` is driven by `verdict[idx - pad]`,
    // which must already be graded. If more verdicts than that exist we are
    // planning too late; if fewer, too early. Either means the request/consume
    // wiring no longer matches `pad` and the delay is being mis-applied. At
    // session start we necessarily request ahead of grading, so a shortfall in
    // the first `pad` requests is expected and only logged.
    const graded = this.stream.getGradedCount();
    const expected = Math.max(0, idx - this.config.pad);
    if (graded !== expected) {
      this.log(
        `[FlowBot] PAD invariant: planning idx=${idx} with ${graded} graded, expected ${expected}`,
      );
    }

    this.stream.register(phraseKey, PhraseKind.Assessment);
    return {
      kind: PhraseKind.Assessment,
      crMin: band.crMin,
      crMax: band.crMax,
      idx,
      requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
    };
  }

  /**
   * Grade a phrase that has finished playing.
   *
   * Rest phrases are recognised and deliberately skipped — they receive no
   * verdict, do not move the band, and do not consume a PAD slot.
   *
   * @param phraseKey the same key passed to {@link planNextPhrase} for this
   *              phrase (the render pipeline's `sequenceId`).
   * @param notes the phrase's graded notes, read from `EventStream`. The tally
   *              uses complexity weights (red 1.0 / yellow 0.5), not
   *              `EventStream`'s accuracy scale.
   */
  onPhraseEnd(phraseKey: string, notes: readonly GradedNote[]): void {
    const entry = this.stream.getEntry(phraseKey);
    if (entry === undefined) {
      this.log(`[FlowBot] ignoring unknown phrase ${phraseKey}`);
      return;
    }

    if (entry.kind === PhraseKind.Rest) {
      this.actor.send({ type: 'REST_COMPLETED' });
      this.log(`[FlowBot] rest phrase complete — band untouched`);
      return;
    }

    // A phrase can finish playing more than once — the cursor re-enters the same
    // phrase on rollback / resume, and each re-entry fires onFinishPlayingPhrase
    // again (the phrase-request path dedupes by index and ATA by uuid for the
    // same reason). One assessment phrase is one verdict, graded on first
    // completion, so ignore any later finish rather than double-grading.
    if (entry.verdict !== undefined) {
      this.log(`[FlowBot] phrase ${phraseKey} already graded — ignoring duplicate finish`);
      return;
    }

    const errorTotal = tallyPhraseErrors(notes);
    const verdict = classify(errorTotal, this.config);
    this.stream.grade(phraseKey, verdict, errorTotal);

    const gradedIdx = entry.idx as number;
    const band = bandFor(gradedIdx + this.config.pad, this.stream.getVerdicts(), this.config);
    this.log(
      `[FlowBot] idx=${gradedIdx} errors=${errorTotal} verdict=${verdict} ` +
        `-> band ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)} (applies at idx=${band.idx})`,
    );
    this.onBandChanged?.({ band, verdict, errorTotal, gradedIdx });
  }

  /** The full band trajectory so far — telemetry, tests, and the stuck check. */
  getTrajectory(): AssessmentBand[] {
    const graded = this.stream.getGradedCount();
    if (graded === 0) return [];
    return computeTrajectory(this.stream.getVerdicts(), this.config, graded);
  }

  /** Band that will apply to a given assessment index. */
  getBand(idx: number): AssessmentBand {
    return bandFor(idx, this.stream.getVerdicts(), this.config);
  }

  getPhase(): ComplexityPhase {
    return this.actor.getSnapshot().value as ComplexityPhase;
  }

  getRestCount(): number {
    return this.actor.getSnapshot().context.restsInjected;
  }

  /**
   * Whether the phrase registered under `phraseKey` is a rest phrase. Used by
   * the scene at play time (keyed by the render pipeline's sequenceId) to drive
   * the rest-phrase UI. Unknown keys are not rest phrases.
   */
  isRest(phraseKey: string): boolean {
    return this.stream.isRest(phraseKey);
  }

  /** Read-only view of the assessment stream, for telemetry and tests. */
  getStream(): AssessmentStream {
    return this.stream;
  }

  getTierConfig(): TierConfig {
    return this.config;
  }

  /**
   * Clear all state. The scene instance is reused across tier changes and class
   * field initialisers do not re-run, so this must be called on tier change.
   */
  reset(): void {
    this.stream.reset();
    this.sessionMap.load([]);
    this.actor.send({ type: 'RESET' });
    this.log('[FlowBot] reset');
  }

  destroy(): void {
    this.actor.stop();
  }
}
